import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { CustomerSession } from "@shared-core/data/customer-api-types";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { AlertTriangle, FileX2 } from "lucide-react";
import { PortalFooter } from "../portal/PortalFooter";

// Chrome for the customer's quote and order pages by their emailed link (Ben
// and Brad, 5 Oct): no sign-in, the token in the URL is the credential. So no
// nav and no account menu, only the brand, and a quiet way in for someone
// who does have a portal login. A signed-in route would render the same page
// bodies inside the normal portal chrome.

export function LinkLayout({
  children,
  signedInWhere,
  isWide = false,
  isSignedIn = false,
  legalDetails,
}: {
  children: React.ReactNode;
  /** Someone is signed in in this browser (an account just created from the page): no sign-in offer, and no
   *  "you don't need to sign in" (docs/features/portal-sign-in/08, `link.invited-order-contact.account-just-created`). */
  isSignedIn?: boolean;
  /** The legal details of the company the page speaks for (the order's, or the quote's customer's default), once the
   *  page has loaded; nothing prints without them. */
  legalDetails?: CustomerSession["legal_details"];
  /** Room for a summary beside the page (the quote's): the page, header and footer run wider. */
  isWide?: boolean;
  /** Where this kind of page lives for a signed-in customer, in their words. */
  signedInWhere: string;
}) {
  const width = isWide ? "max-w-6xl" : "max-w-4xl";
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-card">
        <div
          className={cn(
            "mx-auto flex items-center justify-between gap-4 px-4 py-3 sm:px-6",
            width,
          )}
        >
          <span className="text-sm font-semibold tracking-tight">Pollards</span>
          {isSignedIn ? null : (
            <Link
              to={paths.portalSignIn()}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Sign in to your portal
            </Link>
          )}
        </div>
      </header>
      <main className={cn("mx-auto px-4 py-6 sm:px-6 sm:py-8", width)}>
        {children}
      </main>
      {isSignedIn ? null : (
        <footer
          className={cn(
            "mx-auto px-4 pb-10 text-xs text-muted-foreground sm:px-6",
            width,
          )}
        >
          You opened this page from a link we emailed you, so you don&apos;t
          need to sign in. If you have a Pollards portal login, you&apos;ll also
          find it there under {signedInWhere}.
        </footer>
      )}
      <PortalFooter legalDetails={legalDetails} className={width} />
    </div>
  );
}

/** A link to something closed more than 90 days ago (410): the owner's sentence. */
export function GoneCard({
  sentence,
  what,
}: {
  sentence: string;
  what: string;
}) {
  return (
    <Card className="mx-auto max-w-lg items-center gap-3 p-8 text-center">
      <FileX2 className="size-8 text-muted-foreground" />
      <h1 className="text-lg font-semibold">{sentence}</h1>
      <p className="text-sm text-muted-foreground">
        We keep {what} here for 90 days after they close. If you need a copy,
        reply to the email we sent you and we&apos;ll send one over.
      </p>
    </Card>
  );
}

/** A proof's link that has ended (410): the owner's sentence and, when a newer proof replaced it (`proof_replaced`),
 *  the way to the order, where the newer one is. */
export function ProofEndedCard({
  sentence,
  orderPageUrl,
}: {
  sentence: string;
  orderPageUrl: string | null;
}) {
  return (
    <Card className="mx-auto max-w-lg items-center gap-3 p-8 text-center">
      <FileX2 className="size-8 text-muted-foreground" />
      <h1 className="text-lg font-semibold">{sentence}</h1>
      {orderPageUrl && (
        <Button asChild variant="outline">
          <OrderLink url={orderPageUrl}>Open your order</OrderLink>
        </Button>
      )}
    </Card>
  );
}

/** A link the backend handed out to an order page: this app's own page in place, anything else as a plain link. */
export function OrderLink({
  url,
  ...rest
}: { url: string } & React.ComponentProps<"a">) {
  return url.startsWith("/") ? (
    <Link to={url} {...rest} />
  ) : (
    <a href={url} {...rest} />
  );
}

/** Any link that is not one (unknown, mistyped, forged): the bare 404. */
export function LinkNotFoundCard() {
  return (
    <Card className="mx-auto max-w-lg items-center gap-3 p-8 text-center">
      <AlertTriangle className="size-8 text-muted-foreground" />
      <h1 className="text-lg font-semibold">We can&apos;t find this page</h1>
      <p className="text-sm text-muted-foreground">
        The link may have been copied only in part. Please open it again from
        the email we sent you, or reply to that email and we&apos;ll help.
      </p>
    </Card>
  );
}

export function LinkLoadingCard({ what }: { what: string }) {
  return (
    <Card className="p-8 text-center text-sm text-muted-foreground">
      Loading your {what}…
    </Card>
  );
}

export function LinkErrorCard({
  what,
  message,
  onRetry,
}: {
  what: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card className="items-center gap-3 p-8 text-center">
      <AlertTriangle className="size-6 text-muted-foreground" />
      <p className="font-medium">We couldn&apos;t load this {what}</p>
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}
